/**
 * HIVE rune → Collect handoff, Collect's side (contract HIVE_RUNE_EXPORT v1).
 *
 * The rune's half is landing-hive/runes/_collect-cta.js (same limits, same messages):
 *   1. The visitor confirms "Siųsti į Collect" on a rune at https://hive.qodoonect.com;
 *      the rune opens /send?from=runes&template=<slug>&handoff=1 here with window.open.
 *   2. This page posts { type: 'hive-collect-ready', version: 1 } to window.opener, with
 *      targetOrigin = each allow-listed HIVE origin (never '*').
 *   3. The rune answers that window only: { type: 'hive-collect-payload', version: 1, payload }.
 *      Accepted only from an allow-listed origin AND from window.opener itself; every other
 *      message is ignored. The payload is checked with a strict schema (validateRuneExport).
 *   4. This page answers { type: 'hive-collect-ack', version: 1, ok, error? } to that origin.
 *   5. The send form is prefilled (template, purpose = title, the payload as a compact JSON
 *      file); the visitor still presses Send. The payload lives in memory only (React state),
 *      never in localStorage / sessionStorage. After a full-page reload of this window that
 *      keeps window.opener, the page asks the rune again (step 2): the rune keeps the payload
 *      for that window for 30 minutes. A page with Cross-Origin-Opener-Policy on the way (the
 *      Trust sign-in today) cuts window.opener: then the visitor goes back to the rune.
 *
 * Allow-list: https://hive.qodoonect.com only. Vercel preview origins of the hive project
 * are NOT allowed: a pattern such as hive-landing-*-<team>.vercel.app cannot be matched
 * safely (anyone can create a team or alias whose name ends the same way).
 *
 * Pure module (no DOM, no React) so it is unit-tested with `node --test` (tests/handoff.test.mjs).
 */
import { MAX_SOURCE_FILE_BYTES } from './bundleBuilder.ts';

export const HIVE_ORIGIN = 'https://hive.qodoonect.com';
export const HIVE_ORIGINS: readonly string[] = Object.freeze([HIVE_ORIGIN]);

export interface HandoffLimits {
  /** Unicode code points */
  titleChars: number;
  summaryChars: number;
  /** UTF-8 bytes of JSON.stringify(data) */
  dataBytes: number;
  depth: number;
  createdAtChars: number;
  /** UTF-8 bytes of the attached file (exportAttachment): the send form's per-file limit */
  attachmentBytes: number;
}

export const HANDOFF_LIMITS: Readonly<HandoffLimits> = Object.freeze({
  titleChars: 120,
  summaryChars: 500,
  dataBytes: 256 * 1024,
  depth: 100,
  createdAtChars: 64,
  attachmentBytes: MAX_SOURCE_FILE_BYTES,
});

export const RUNE_KINDS = ['tree', 'graph', 'scan', 'flow', 'notes', 'score', 'chart', 'other'] as const;
export type RuneKind = (typeof RUNE_KINDS)[number];

const FIELDS = ['version', 'rune', 'title', 'summary', 'kind', 'data', 'createdAt'] as const;

export const MSG_READY = 'hive-collect-ready';
export const MSG_PAYLOAD = 'hive-collect-payload';
export const MSG_ACK = 'hive-collect-ack';

/** How long this page waits for the rune's payload before it says so (it still accepts a late one). */
export const HANDOFF_WAIT_MS = 15_000;

export interface RuneExport {
  version: 1;
  rune: string;
  title: string;
  summary: string;
  kind: RuneKind;
  data: Record<string, unknown> | unknown[];
  createdAt: string;
}

export type ExportError =
  | 'not_object'
  | 'unknown_field'
  | 'missing_field'
  | 'bad_version'
  | 'bad_rune'
  | 'rune_mismatch'
  | 'bad_title'
  | 'bad_summary'
  | 'bad_kind'
  | 'bad_data'
  | 'too_large'
  | 'bad_created_at';

export type ExportCheck =
  | { ok: true; payload: RuneExport; dataBytes: number; fileBytes: number }
  | { ok: false; error: ExportError; message: string };

const SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,9})?)?(Z|([+-])(\d{2}):(\d{2}))$/;
// Never in a title or a summary: C0 controls (a summary keeps tab, line feed, carriage
// return), DEL, C1 controls, U+2028/U+2029 and the bidi embedding/override/isolate controls.
const BAD_TITLE_RE = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/;
const BAD_TEXT_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/;
// A title of only white space and format characters (Cf: zero-width space, joiners, BOM …) is blank.
const BLANK_RE = /^[\s\p{Cf}]*$/u;

/** Exact match against the allow-list: scheme, host and port. */
export function isHiveOrigin(origin: unknown, allow: readonly string[] = HIVE_ORIGINS): boolean {
  return typeof origin === 'string' && origin !== '*' && origin !== 'null' && allow.includes(origin);
}

export function codePoints(s: string): number {
  let n = 0;
  for (const _ of s) n++;
  return n;
}

export function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  if (Object.prototype.toString.call(v) !== '[object Object]') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === null || Object.getPrototypeOf(proto) === null;
}

/** A real calendar date-time: 2026-02-30 or 24:00 are refused even where Date.parse rolls them over. */
export function isIsoDateTime(s: string): boolean {
  const m = ISO_RE.exec(s);
  if (!m) return false;
  const [y, mo, d, h, mi] = [m[1], m[2], m[3], m[4], m[5]].map(Number);
  const se = m[6] === undefined ? 0 : Number(m[6]);
  if (mo < 1 || mo > 12 || d < 1 || h > 23 || mi > 59 || se > 59) return false;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  if (d > [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo - 1]) return false;
  if (m[9] && (Number(m[10]) > 23 || Number(m[11]) > 59)) return false;
  return Number.isFinite(Date.parse(s));
}

type Copy = { value: unknown; problem?: undefined } | { value?: undefined; problem: string };

/**
 * Reads `v` exactly once and returns a fresh plain-JSON copy, or why it is not plain JSON.
 * `undefined` is handled as JSON.stringify does: dropped in objects, null in arrays.
 */
function copyJson(v: unknown, depth: number, maxDepth: number, stack: unknown[]): Copy {
  if (v === null) return { value: null };
  const t = typeof v;
  if (t === 'string' || t === 'boolean') return { value: v };
  if (t === 'number') return Number.isFinite(v) ? { value: v } : { problem: 'a number that is NaN or Infinity' };
  if (t === 'function') return { problem: 'a function' };
  if (t !== 'object') return { problem: `a value of type ${t}` };
  if (depth > maxDepth) return { problem: `nesting deeper than ${maxDepth}` };
  if (stack.includes(v)) return { problem: 'a circular reference' };
  const arr = Array.isArray(v);
  if (!arr && !isPlainObject(v)) return { problem: 'an object that is not plain JSON' };
  stack.push(v);
  const out: unknown[] | Record<string, unknown> = arr ? [] : {};
  const keys = arr ? null : Object.keys(v as Record<string, unknown>);
  const n = arr ? (v as unknown[]).length : keys!.length;
  for (let i = 0; i < n; i++) {
    const item = arr ? (v as unknown[])[i] : (v as Record<string, unknown>)[keys![i]];
    if (item === undefined) {
      if (arr) (out as unknown[]).push(null);
      continue;
    }
    const r = copyJson(item, depth + 1, maxDepth, stack);
    if (r.problem !== undefined) return r;
    if (arr) (out as unknown[]).push(r.value);
    else Object.defineProperty(out, keys![i], { value: r.value, enumerable: true, writable: true, configurable: true });
  }
  stack.pop();
  return { value: out };
}

function fail(error: ExportError, message: string): ExportCheck {
  return { ok: false, error, message };
}

function kb(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function check(p: unknown, expectedRune: string | undefined, L: HandoffLimits): ExportCheck {
  if (!isPlainObject(p)) return fail('not_object', 'The rune did not send an object.');
  for (const k of Object.keys(p)) {
    if (!(FIELDS as readonly string[]).includes(k)) return fail('unknown_field', 'The rune sent a field that is not in the contract.');
  }
  // Each field is read once: what is checked is what is shown and attached.
  const f: Record<string, unknown> = {};
  for (const k of FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(p, k)) return fail('missing_field', `The rune's data has no "${k}".`);
    f[k] = p[k];
  }
  if (f.version !== 1) return fail('bad_version', 'The rune uses another version of the export format.');
  if (typeof f.rune !== 'string' || !SLUG_RE.test(f.rune)) return fail('bad_rune', 'The rune name is not valid.');
  if (expectedRune !== undefined && f.rune !== expectedRune) {
    return fail('rune_mismatch', 'The data comes from another rune than the one in the link.');
  }
  if (typeof f.title !== 'string' || BLANK_RE.test(f.title) || codePoints(f.title) > L.titleChars || BAD_TITLE_RE.test(f.title)) {
    return fail('bad_title', `The title must be one visible line of 1–${L.titleChars} characters, without control or direction characters.`);
  }
  if (typeof f.summary !== 'string' || codePoints(f.summary) > L.summaryChars || BAD_TEXT_RE.test(f.summary)) {
    return fail('bad_summary', `The summary must be plain text of at most ${L.summaryChars} characters, without control or direction characters.`);
  }
  if (typeof f.kind !== 'string' || !(RUNE_KINDS as readonly string[]).includes(f.kind)) return fail('bad_kind', 'The kind of data is not one Collect knows.');
  if (!isPlainObject(f.data) && !Array.isArray(f.data)) return fail('bad_data', 'The data must be a JSON object or array.');
  const c = copyJson(f.data, 1, L.depth, []);
  if (c.problem !== undefined) return fail('bad_data', `The data is not plain JSON: it contains ${c.problem}.`);
  const bytes = utf8Bytes(JSON.stringify(c.value));
  if (bytes > L.dataBytes) {
    return fail('too_large', `The data is too large: ${kb(bytes)}, the limit is ${kb(L.dataBytes)}.`);
  }
  if (typeof f.createdAt !== 'string' || f.createdAt.length > L.createdAtChars || !isIsoDateTime(f.createdAt)) {
    return fail('bad_created_at', 'The creation time is not a real ISO date-time.');
  }
  const payload: RuneExport = {
    version: 1,
    rune: f.rune,
    title: f.title,
    summary: f.summary,
    kind: f.kind as RuneKind,
    data: c.value as RuneExport['data'],
    createdAt: f.createdAt,
  };
  const fileBytes = utf8Bytes(exportAttachment(payload).text);
  if (fileBytes > L.attachmentBytes) {
    return fail('too_large', `The export file is too large for the send form: ${kb(fileBytes)}, the limit is ${kb(L.attachmentBytes)}.`);
  }
  return { ok: true, payload, dataBytes: bytes, fileBytes };
}

/**
 * Strict schema check of a rune export (same rules as the rune's validate()). Returns a
 * fresh JSON copy on success. `expectedRune` = the `template` of this page's link.
 * `limits` overrides some of HANDOFF_LIMITS (tests). Messages are fixed texts: nothing from
 * the payload is echoed.
 */
export function validateRuneExport(p: unknown, expectedRune?: string, limits?: Partial<HandoffLimits>): ExportCheck {
  const L: HandoffLimits = limits ? { ...HANDOFF_LIMITS, ...limits } : HANDOFF_LIMITS;
  try {
    return check(p, expectedRune, L);
  } catch {
    return fail('bad_data', 'The rune\'s data could not be read as JSON.');
  }
}

/**
 * The JSON file attached to the capsule: the whole export as compact JSON (pretty-printing
 * can grow a 256 KB export past the send form's 5 MB file limit), named after the rune and
 * its time. The rune's "Atsisiųsti JSON" saves the same bytes.
 */
export function exportAttachment(p: RuneExport): { name: string; text: string; type: 'application/json' } {
  return {
    name: `${p.rune}-${p.createdAt.replace(/[:.]/g, '-')}.json`,
    text: `${JSON.stringify(p)}\n`,
    type: 'application/json',
  };
}

/** What the send form takes from an export: template = rune slug, purpose = title, the file. */
export function prefillFromExport(p: RuneExport) {
  return { template: p.rune, purpose: p.title, file: exportAttachment(p) };
}

/**
 * Is the export's file in the send form? 'skipped' = the form refused it (over its file
 * limit — not reachable with a validated export, kept honest anyway), 'removed' = the
 * visitor took it out, 'attached' = it is in the list.
 */
export function attachmentStatus(
  file: { name: string; size: number },
  files: ReadonlyArray<{ name: string; size: number }>,
  maxBytes: number = MAX_SOURCE_FILE_BYTES,
): 'attached' | 'skipped' | 'removed' {
  if (file.size > maxBytes) return 'skipped';
  return files.some((f) => f.name === file.name && f.size === file.size) ? 'attached' : 'removed';
}

export function formatKB(bytes: number): string {
  return kb(bytes);
}

// ─── the receiver ──────────────────────────────────────────────────────────

export interface HandoffTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface HandoffMessage {
  origin: string;
  source: unknown;
  data: unknown;
}

/** The parts of `window` the receiver uses (a fake in the tests). */
export interface HandoffWindow {
  opener: HandoffTarget | null;
  addEventListener(type: 'message', fn: (ev: HandoffMessage) => void): void;
  removeEventListener(type: 'message', fn: (ev: HandoffMessage) => void): void;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: any): void;
}

export type HandoffState =
  | { status: 'waiting' }
  | { status: 'no-opener' }
  | { status: 'timeout' }
  | { status: 'invalid'; error: ExportError; message: string }
  | { status: 'received'; payload: RuneExport; dataBytes: number; fileBytes: number };

export interface ReceiverOptions {
  onState: (s: HandoffState) => void;
  /** The `template` of this page's link; the payload's `rune` must equal it. */
  expectedRune?: string;
  /** Tests only; production uses HIVE_ORIGINS. */
  origins?: readonly string[];
  waitMs?: number;
}

function post(target: HandoffTarget, message: unknown, origin: string): void {
  try {
    target.postMessage(message, origin);
  } catch {
    /* the opener went away or navigated: nothing to tell */
  }
}

/**
 * Listen for one rune payload from window.opener and announce readiness to it.
 * Returns a stop function (removes the listener and the timer).
 */
export function startHandoffReceiver(win: HandoffWindow, opts: ReceiverOptions): () => void {
  const origins = (opts.origins ?? HIVE_ORIGINS).filter((o) => /^https?:\/\/[^/*\s]+$/.test(o));
  const opener = win.opener;
  if (!opener) {
    opts.onState({ status: 'no-opener' });
    return () => {};
  }

  let done = false;
  let answered = false;
  let timer: unknown = null;

  const onMessage = (ev: HandoffMessage) => {
    if (!ev || !isHiveOrigin(ev.origin, origins) || ev.source !== opener) return;
    const d = ev.data as { type?: unknown; payload?: unknown } | null;
    if (!d || typeof d !== 'object' || d.type !== MSG_PAYLOAD) return;
    if (done) return; // the first valid payload of this page load wins
    answered = true;
    const r = validateRuneExport(d.payload, opts.expectedRune);
    if (!r.ok) {
      post(opener, { type: MSG_ACK, version: 1, ok: false, error: r.error }, ev.origin);
      opts.onState({ status: 'invalid', error: r.error, message: r.message });
      return;
    }
    done = true;
    if (timer !== null) win.clearTimeout(timer);
    post(opener, { type: MSG_ACK, version: 1, ok: true }, ev.origin);
    opts.onState({ status: 'received', payload: r.payload, dataBytes: r.dataBytes, fileBytes: r.fileBytes });
  };

  win.addEventListener('message', onMessage);
  opts.onState({ status: 'waiting' });
  // targetOrigin = each allowed origin: the browser delivers it only if the opener is that origin.
  for (const o of origins) post(opener, { type: MSG_READY, version: 1 }, o);
  timer = win.setTimeout(() => {
    timer = null;
    if (!done && !answered) opts.onState({ status: 'timeout' });
  }, opts.waitMs ?? HANDOFF_WAIT_MS);

  return () => {
    win.removeEventListener('message', onMessage);
    if (timer !== null) win.clearTimeout(timer);
  };
}
